import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { hierarchyService } from '../../../services/hierarchyService';
import { roomService } from '../../../services/roomService';

const RoomModal = ({ isOpen, onClose, onSave, room, fixedDepartment = null }) => {
    const [formData, setFormData] = useState({
        name: '',
        departmentId: '',
        departmentName: '', // Useful for display
        departmentCode: '', // Essential for ID generation
        type: '',
        capacity: 30,
        status: 'active',
        location: '',
        generatedId: ''
    });

    const [departments, setDepartments] = useState([]);
    const [roomTypes, setRoomTypes] = useState([]);
    const [loadingDepts, setLoadingDepts] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            fetchDepartments();
            roomService.getRoomTypes().then(setRoomTypes).catch(err => setError(err.message || 'Failed to load room types'));
            if (room) {
                // Edit mode
                setFormData({
                    name: room.name || '',
                    departmentId: room.departmentId || '',
                    departmentName: room.departmentName || '',
                    departmentCode: room.id ? room.id.split('-')[0] : '', // Extract from ID if not saved separately
                    type: room.type || '',
                    capacity: room.capacity || 30,
                    status: String(room.status || 'active').toLowerCase(),
                    location: room.location || '',
                    generatedId: room.id || ''
                });
            } else {
                const locked= fixedDepartment;
                // Create mode
                setFormData({
                    name: '',
                    departmentId: locked?.id || '',
                    departmentName: locked?.name || '',
                    departmentCode: locked?.code || (locked?.name ? locked.name.substring(0,4).toUpperCase() : ''),
                    type: '',
                    capacity: 30,
                    status: 'active',
                    location: '',
                    generatedId: ''
                });
            }
            setError(null);
        }
    }, [isOpen, room]);

    // Effect to generate ID when department changes
    useEffect(() => {
        const updateId = async () => {
            // If we have a selected department
            if (formData.departmentCode) {
                // Check if we are in edit mode and the department is the original one
                if (room && formData.departmentId === room.departmentId) {
                    setFormData(prev => ({ ...prev, generatedId: room.id }));
                } else {
                    // Otherwise (Create mode OR Edit mode with different department), generate new ID
                    const id = await roomService.generateRoomId(formData.departmentCode);
                    setFormData(prev => ({ ...prev, generatedId: id }));
                }
            }
        };
        updateId();
    }, [formData.departmentCode, formData.departmentId, room]);

    const fetchDepartments = async () => {
        try {
            setLoadingDepts(true);
            const data = await hierarchyService.getDepartments();
            setDepartments(data || []);
        } catch (err) {
            console.error('Error fetching departments:', err);
            setError('Failed to load departments');
        } finally {
            setLoadingDepts(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'number' ? (value ? parseInt(value, 10) : '') : value
        }));
    };

    const handleDepartmentChange = (e) => {
        const deptId = e.target.value;
        const dept = departments.find(d => d.id === deptId);
        if (dept) {
            // Assuming department object has a 'code' property or we create one from name
            const code = dept.code || dept.name.substring(0, 4).toUpperCase();
            setFormData(prev => ({
                ...prev,
                departmentId: deptId,
                departmentName: dept.name,
                departmentCode: code
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                departmentId: '',
                departmentName: '',
                departmentCode: '',
                generatedId: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) return setError('Room Name is required');
        if (!formData.departmentId) return setError('Department is required');
        if (!formData.type) return setError('Room Type is required');

        try {
            setLoading(true);
            await onSave({
                ...formData,
                id: formData.generatedId // Always use the current generatedId (whether original or new)
            });
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to save room');
        } finally {
            setLoading(false);
        }
    };

    const groupedRoomTypes = roomTypes.reduce((groups, item) => {
        if (!groups[item.category]) groups[item.category] = [];
        groups[item.category].push(item);
        return groups;
    }, {});

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-lg w-full max-w-md max-h-[92vh] overflow-y-auto p-6">
                <h3 className="text-xl font-bold text-foreground mb-4">{room ? 'Edit Room' : 'Add Room'}</h3>

                {error && (
                    <div className="bg-error/10 text-error text-sm p-3 rounded-md mb-4">{error}</div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Department</label>
                        <select
                            value={formData.departmentId}
                            onChange={handleDepartmentChange}
                            disabled={loadingDepts} // Enabled in edit mode now
                            className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground"
                            required
                        >
                            <option value="">Select Department</option>
                            {departments.map(d => (
                                <option key={d.id} value={d.id}>{d.name}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Room ID</label>
                        <Input
                            value={formData.generatedId}
                            disabled
                            className="bg-muted"
                            placeholder="Auto-generated (e.g., COMP-001)"
                        />
                        <p className="text-xs text-muted-foreground mt-1">ID is auto-generated based on Department Code</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Room Name</label>
                        <Input
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="e.g., Lab 1"
                            required
                        />
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-foreground mb-1">Room Type</label>
                            <select
                                name="type"
                                value={formData.type}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground"
                                required
                            >
                                <option value="">Select Type</option>
                                {Object.entries(groupedRoomTypes).map(([category, types]) => (
                                    <optgroup key={category} label={category}>
                                        {types.map(type => (
                                            <option key={type.id || type.name} value={type.name}>{type.name}</option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                        </div>
                    </div>

                    {!fixedDepartment && <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Capacity</label>
                        <Input
                            type="number"
                            name="capacity"
                            value={formData.capacity}
                            onChange={handleChange}
                            min="1"
                            required
                        />
                    </div>}
                    {fixedDepartment && <div className="rounded-lg border border-blue-200 bg-blue-50 p-3"><p className="text-xs font-semibold uppercase text-blue-700">Department</p><p className="mt-1 font-medium text-blue-950">{fixedDepartment.name}</p><p className="mt-1 text-xs text-blue-700">Automatically assigned from your staff scope.</p></div>}

                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Location / Building</label>
                        <Input name="location" value={formData.location} onChange={handleChange} placeholder="e.g., Engineering Block, Ground Floor" />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Operational Status</label>
                        <select name="status" value={formData.status} onChange={handleChange} className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground" required>
                            <option value="active">Active</option><option value="inactive">Inactive</option><option value="maintenance">Under maintenance</option>
                        </select>
                    </div>

                    <div className="flex justify-end space-x-3 mt-6">
                        <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
                        <Button variant="default" type="submit" disabled={loading}>
                            {loading ? 'Saving...' : (room ? 'Update Room' : 'Create Room')}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RoomModal;
